import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Building2, CalendarDays, TrendingUp, Users, ArrowUpRight, ArrowDownLeft, Percent } from "lucide-react";
import axios from "axios";

function fmtDate(d) { if (!d) return ""; try { const [y, m, day] = d.split("-"); return `${day}/${m}/${y}`; } catch { return d; } }

export default function DashboardPage() {
  const { user, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";
  const displayName = user?.cognome ? `${user.name} ${user.cognome}` : user?.name;

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`)
      .then(r => setStats(r.data))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API]);

  const EUR = (v) => `\u20AC${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  const s = stats || {};
  const kpis = [
    { title: "Proprietà", value: s.properties_count || 0, icon: Building2, bg: "bg-stone-100", color: "text-stone-700" },
    { title: "Prenotazioni Mese", value: s.monthly_bookings || 0, icon: CalendarDays, bg: "bg-blue-50", color: "text-blue-600" },
    { title: "Guadagni Mese", value: EUR(s.monthly_earnings), icon: TrendingUp, bg: "bg-green-50", color: "text-green-600" },
    ...(isAdmin ? [{ title: "Proprietari", value: s.owners_count || 0, icon: Users, bg: "bg-amber-50", color: "text-amber-600" }] : []),
    { title: "Occupazione", value: `${s.occupancy_rate || 0}%`, icon: Percent, bg: "bg-purple-50", color: "text-purple-600", sub: `${s.occupied_count || 0}/${s.total_properties || 0} occupate` },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">
          Ciao, {displayName}
        </h1>
        <p className="text-stone-500 mt-1 text-sm">
          Ecco il riepilogo delle tue {isAdmin ? "proprietà in gestione" : "proprietà"}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {kpis.map((kpi, i) => (
          <Card key={i} data-testid={`dashboard-kpi-${i}`} className="border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className={`h-9 w-9 rounded-sm ${kpi.bg} flex items-center justify-center mb-3`}>
                <kpi.icon className={`h-[18px] w-[18px] ${kpi.color}`} strokeWidth={1.5} />
              </div>
              <p className="font-mono text-2xl font-bold text-stone-900">{kpi.value}</p>
              <p className="text-xs text-stone-500 mt-1">{kpi.title}</p>
              {kpi.sub && <p className="text-[10px] text-stone-400 mt-0.5">{kpi.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Today Section */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="border border-stone-200 shadow-sm" data-testid="today-checkins-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-900 flex items-center gap-2">
              <ArrowDownLeft className="h-4 w-4 text-green-500" /> Check-in Oggi
              <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200 ml-auto">
                {(s.today_checkins || []).length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(s.today_checkins || []).length === 0 ? (
              <p className="text-sm text-stone-400 py-4 text-center">Nessun check-in oggi</p>
            ) : (
              <div className="space-y-2">
                {s.today_checkins.map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-green-50/50 rounded-sm border border-green-100">
                    <div>
                      <p className="text-sm font-medium text-stone-900">{b.guest_name}</p>
                      <p className="text-xs text-stone-500">{b.property_name}</p>
                    </div>
                    <span className="text-xs font-mono text-green-700">{EUR(b.total_amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-stone-200 shadow-sm" data-testid="today-checkouts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-900 flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-red-500" /> Check-out Oggi
              <Badge className="text-[10px] bg-red-50 text-red-700 border-red-200 ml-auto">
                {(s.today_checkouts || []).length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(s.today_checkouts || []).length === 0 ? (
              <p className="text-sm text-stone-400 py-4 text-center">Nessun check-out oggi</p>
            ) : (
              <div className="space-y-2">
                {s.today_checkouts.map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-red-50/50 rounded-sm border border-red-100">
                    <div>
                      <p className="text-sm font-medium text-stone-900">{b.guest_name}</p>
                      <p className="text-xs text-stone-500">{b.property_name}</p>
                    </div>
                    <span className="text-xs font-mono text-stone-600">{fmtDate(b.check_out)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary + Recent Bookings */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="border border-stone-200 shadow-sm" data-testid="revenue-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-900">Riepilogo Finanziario</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-sm text-stone-500">Fatturato Totale</span>
                <span className="font-mono text-sm font-semibold text-stone-900">{EUR(s.total_revenue)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-sm text-stone-500">Netto Totale</span>
                <span className="font-mono text-sm font-semibold text-amber-700">{EUR(s.total_earnings)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-stone-100">
                <span className="text-sm text-stone-500">Mese Corrente</span>
                <span className="font-mono text-sm font-semibold text-green-700">{EUR(s.monthly_total)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-stone-500">Prenotazioni Attive</span>
                <span className="font-mono text-sm font-bold text-blue-600">{s.active_bookings || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-stone-200 shadow-sm" data-testid="recent-bookings-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-900">Prenotazioni Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            {(s.recent_bookings || []).length === 0 ? (
              <p className="text-sm text-stone-400 py-4 text-center">Nessuna prenotazione recente</p>
            ) : (
              <div className="space-y-2">
                {(s.recent_bookings || []).slice(0, 6).map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-sm hover:bg-stone-50 transition-colors border-b border-stone-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-sm bg-stone-100 flex items-center justify-center text-xs font-semibold text-stone-600">
                        {b.guest_name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{b.guest_name}</p>
                        <p className="text-xs text-stone-500">{b.property_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-medium text-stone-900">{EUR(b.total_amount)}</p>
                      <p className="text-[10px] text-stone-400">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
